//@flow
import React, { Component } from 'react';
import { withRouter, type Location } from 'common/components/core/router';
import { Switch, Route, Redirect } from 'react-router-dom';

import type { List } from 'immutable';
import type { Match } from 'react-router-dom';

import { Loader } from 'common/components/core/loader/Loader';
import { Box } from '@locus-taxy/neo-ui';

import type { FpPjPlan } from 'common/models/flow/FpPjPlan';

import FpAllocatedVisitsContainer from './FpAllocatedVisitsContainer';
import FpUnallocatedVisitsContainer from './FpUnallocatedVisitsContainer';
import type { FpSalesbeatPlan } from 'common/models/flow/FpSalesbeatPlan';
import { FpContainers } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';
import { PlanEditContext } from './planEdits/PlanEditContext';

type State = {
    chartZoomLevel: number,
    sortBy: string,
    exportErrorMessage: ?string,
    selectedVisitsView: string
};

type Props = {
    clientId: ?string,
    match: Match,
    pjpId: string,
    plan: ?FpPjPlan,
    salesbeatPlan: ?FpSalesbeatPlan,
    selectedTourIds: List<string>,
    hoveredTour: number,
    isOriginal: boolean,
    forceFitBounds: boolean,
    rightPaneWidth: number,
    leftPaneWidth: number,
    closeMapSpiderPopup: () => void,
    closeAllMapPopups: () => void,
    onTourSelectionToggle: string => void,
    onTourSelect: string => void,
    setHoveredTour: number => void,
    onVisitTempHover: (number, number) => void,
    selectTourVisitAndCloseSpiderPopup: (number, number, string) => void,
    showToastAction: Function,
    updateToastAction: Function,
    hideToastAction: Function,
    location: Location,
    history: any,
    defaultEmail?: string,
    planTours: Object,
    versionedZone: Object,
    planHasBeenFrozen: boolean,
    updateAsMapMoves: boolean,
    setUpdateAsMapMoves: (updateAsMapMoves: boolean) => void,
    routeRestrictions: List<any>,
    isExportInProgress: boolean,
    setBoundStrategyAndUpdate: () => void
};

export const FpPlanSortByEnum = Object.freeze({
    DayAsc: 'DAY_ASC',
    DayDesc: 'DAY_DESC',
    SalesmanAsc: 'SALESMAN_ASC',
    SalesmanDesc: 'SALESMAN_DESC',
    SalesCategoryAsc: 'SALES_CATEGORY_ASC',
    SalesCategoryDesc: 'SALES_CATEGORY_DESC',
    Default: 'DEFAULT'
});

class FpPlanViewVisitRouteContainer extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const { location } = props;
        const { pathname } = location;
        const visit = pathname.split('/').pop();
        this.state = {
            chartZoomLevel: 6,
            selectedVisitsView:
                visit === FpContainers.Unallocated
                    ? FpContainers.Unallocated
                    : FpContainers.Allocated,
            sortBy: FpPlanSortByEnum.Default,
            exportErrorMessage: null
        };
    }

    static getDerivedStateFromProps(nextProps: Props) {
        const { location: nextLocation } = nextProps;
        const nextVisitView = nextLocation.pathname.split('/').pop();
        return {
            selectedVisitsView: nextVisitView
        };
    }
    componentDidUpdate(prevProps: Props) {
        const { clientId: prevClientId, pjpId: prevPjpId } = prevProps;
        const { clientId, pjpId, closeAllMapPopups } = this.props;
        if (clientId !== prevClientId || pjpId !== prevPjpId) {
            closeAllMapPopups();
        }
    }

    componentWillUnmount() {
        const { closeAllMapPopups } = this.props;
        //selected states
        closeAllMapPopups();
        //@fix: reset filters
    }

    static contextType = PlanEditContext;

    changeVisitsView = (visit: string) => {
        this.setState(
            {
                selectedVisitsView: visit
            },
            () => {
                const { match, history } = this.props;
                const path = `${match.url}/${visit}`;
                history.push(path);
            }
        );
    };

    onZoomLevelChanged = (newZoomLevel: number) => {
        this.setState({ chartZoomLevel: newZoomLevel });
    };

    renderLoading = () => {
        const { theme } = this.props;
        return (
            <div className={theme.loading}>
                <Loader active theme={theme} />
            </div>
        );
    };

    noOp: () => void = () => {};

    renderAllocatedVisitsContainer: () => React.Node = () => {
        const { chartZoomLevel, selectedVisitsView } = this.state;
        return (
            <FpAllocatedVisitsContainer
                {...this.props}
                selectedVisitsView={selectedVisitsView}
                changeVisitsView={this.changeVisitsView}
                zoomLevel={chartZoomLevel}
                onZoomLevelChanged={this.onZoomLevelChanged}
                unallocateVisit={this.context.addEdit}
                moveVisit={this.context.addEdit}
                maxEditLimitReached={this.context.maxEditLimitReached}
                edits={this.context.edits}
            />
        );
    };

    renderUnallocatedVisitsContainer: () => React.Node = () => {
        const { chartZoomLevel, selectedVisitsView } = this.state;
        return (
            <FpUnallocatedVisitsContainer
                {...this.props}
                selectedVisitsView={selectedVisitsView}
                changeVisitsView={this.changeVisitsView}
                zoomLevel={chartZoomLevel}
                onZoomLevelChanged={this.onZoomLevelChanged}
                unallocateVisit={this.context.addEdit}
                moveVisit={this.context.addEdit}
                maxEditLimitReached={this.context.maxEditLimitReached}
            />
        );
    };

    render() {
        const { match } = this.props;
        return (
            <Box height="fluid">
                <Switch>
                    <Route
                        exact
                        path={`${match.path}/allocated`}
                        render={this.renderAllocatedVisitsContainer}
                    />
                    <Route
                        exact
                        path={`${match.path}/unallocated`}
                        render={this.renderUnallocatedVisitsContainer}
                    />
                    <Redirect
                        from={`${match.url}`}
                        to={{
                            pathname: `${match.url}/allocated`,
                            state: { from: location }
                        }}
                    />
                </Switch>
            </Box>
        );
    }
}

export default withRouter(FpPlanViewVisitRouteContainer);
